import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Edit, Trash2, Server, Monitor, Laptop, Router, Wifi, HardDrive, Printer, Phone, Shield, Network, MapPin, AlertTriangle, Clock, Building2, Globe, Lock, Copy, Check, Cpu } from 'lucide-react';
import { documentationAPI } from '../../services/api';
import { useAuthStore } from '../../context/authStore';
import RelatedItems from '../../components/documentation/RelatedItems';
import Attachments from '../../components/documentation/Attachments';
import { isInternalRole } from '../../utils/roles.js';

// Icon mapping for configuration types
const TYPE_ICONS = {
  'Server': Server,
  'Workstation': Monitor,
  'Laptop': Laptop,
  'Firewall': Shield,
  'Switch': Network,
  'Router': Router,
  'Wireless AP': Wifi,
  'NAS/Storage': HardDrive,
  'Printer': Printer,
  'Phone/VoIP': Phone,
  default: Server
};

function getTypeIcon(typeName) {
  return TYPE_ICONS[typeName] || TYPE_ICONS.default;
}

// Visibility badge
function VisibilityBadge({ visibility }) {
  const config = {
    private: { icon: Lock, label: 'Internal Only', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    company: { icon: Building2, label: 'Company Visible', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    public: { icon: Globe, label: 'Public', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  };
  const { icon: Icon, label, className } = config[visibility] || config.private;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${className}`}>
      <Icon className="w-4 h-4" />
      {label}
    </span>
  );
}

// Status badge
function StatusBadge({ status }) {
  const statusConfig = {
    'Active': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    'Inactive': 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300',
    'Retired': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig[status] || statusConfig['Active']}`}>
      {status || 'Active'}
    </span>
  );
}

// Copyable field
function CopyableField({ label, value, icon: Icon }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!value) return null;

  return (
    <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
      <div className="flex items-center gap-3">
        {Icon && <Icon className="w-4 h-4 text-neutral-400" />}
        <div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">{label}</p>
          <p className="font-mono text-neutral-900 dark:text-white">{value}</p>
        </div>
      </div>
      <button
        onClick={handleCopy}
        className="p-1.5 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
        title="Copy"
      >
        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

// Info row
function InfoRow({ label, value, icon: Icon }) {
  if (!value) return null;

  return (
    <div className="flex items-start gap-3 py-2">
      {Icon && <Icon className="w-4 h-4 text-neutral-400 mt-0.5" />}
      <div>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">{label}</p>
        <p className="text-neutral-900 dark:text-white">{value}</p>
      </div>
    </div>
  );
}

export default function ConfigurationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isInternal = isInternalRole(user?.role);

  // Fetch configuration
  const { data, isLoading, error } = useQuery({
    queryKey: ['documentation', 'configuration', id],
    queryFn: () => documentationAPI.getConfiguration(id),
  });

  const config = data?.data?.configuration;

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => documentationAPI.deleteConfiguration(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'configurations'] });
      navigate('/documentation/configurations');
    },
  });

  if (isLoading) {
    return (
      <div className="animate-fade-in">
        <div className="mb-6">
          <div className="h-8 bg-neutral-200 dark:bg-neutral-700 rounded w-64 animate-pulse" />
        </div>
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
          <div className="h-8 bg-neutral-200 dark:bg-neutral-700 rounded w-1/2 mb-4 animate-pulse" />
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-16 bg-neutral-100 dark:bg-neutral-900 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="animate-fade-in">
        <div className="text-center py-12">
          <Server className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
          <h2 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">Configuration Not Found</h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            The configuration you're looking for doesn't exist or you don't have access to it.
          </p>
          <Link
            to="/documentation/configurations"
            className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Configurations
          </Link>
        </div>
      </div>
    );
  }

  const TypeIcon = getTypeIcon(config.configuration_type_name);
  const warrantyDaysLeft = config.warranty_expiration
    ? Math.ceil((new Date(config.warranty_expiration) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className="animate-fade-in">
      {/* Back link */}
      <Link
        to="/documentation/configurations"
        className="inline-flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Configurations
      </Link>

      {/* Header */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 mb-6">
        <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-neutral-100 dark:bg-neutral-700">
                <TypeIcon className="w-8 h-8 text-neutral-600 dark:text-neutral-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
                  {config.name}
                </h1>
                {config.hostname && (
                  <p className="text-lg text-neutral-600 dark:text-neutral-400 font-mono mb-3">
                    {config.hostname}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-3">
                  <StatusBadge status={config.configuration_status} />
                  {config.configuration_type_name && (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300">
                      {config.configuration_type_name}
                    </span>
                  )}
                  {isInternal && <VisibilityBadge visibility={config.visibility} />}
                  {warrantyDaysLeft !== null && warrantyDaysLeft <= 90 && (
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                      warrantyDaysLeft <= 0
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        : warrantyDaysLeft <= 30
                          ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    }`}>
                      <AlertTriangle className="w-4 h-4" />
                      Warranty {warrantyDaysLeft <= 0 ? 'Expired' : `expires in ${warrantyDaysLeft} days`}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            {isInternal && (
              <div className="flex items-center gap-2">
                <Link
                  to={`/documentation/configurations?edit=${id}`}
                  className="p-2 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Edit className="w-5 h-5" />
                </Link>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {/* Quick metadata */}
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-neutral-500 dark:text-neutral-400">
            {config.company_name && (
              <span className="flex items-center gap-1.5">
                <Building2 className="w-4 h-4" />
                {config.company_name}
              </span>
            )}
            {config.location_name && (
              <span className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />
                {config.location_name}
              </span>
            )}
            {config.updated_at && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                Updated {new Date(config.updated_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Network Info */}
          {(config.primary_ip || config.primary_mac) && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Network</h2>
              <div className="space-y-3">
                <CopyableField label="IP Address" value={config.primary_ip} icon={Network} />
                <CopyableField label="MAC Address" value={config.primary_mac} icon={Network} />
              </div>
            </div>
          )}

          {/* Hardware Info */}
          {(config.manufacturer || config.model || config.serial_number || config.asset_tag) && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Hardware</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoRow label="Manufacturer" value={config.manufacturer} icon={Cpu} />
                <InfoRow label="Model" value={config.model} icon={Cpu} />
                <CopyableField label="Serial Number" value={config.serial_number} />
                <CopyableField label="Asset Tag" value={config.asset_tag} />
              </div>
            </div>
          )}

          {/* Operating System */}
          {(config.operating_system || config.os_version) && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Operating System</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoRow label="Operating System" value={config.operating_system} icon={Monitor} />
                <InfoRow label="Version" value={config.os_version} />
              </div>
            </div>
          )}

          {/* Notes */}
          {config.notes && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Notes</h2>
              <div className="prose dark:prose-invert max-w-none">
                <p className="whitespace-pre-wrap text-neutral-600 dark:text-neutral-400">{config.notes}</p>
              </div>
            </div>
          )}

          {/* Related Items */}
          <RelatedItems
            sourceType="configuration"
            sourceId={id}
            companyId={config.company_id}
          />

          {/* Attachments */}
          <Attachments
            assetType="configuration"
            assetId={id}
            companyId={config.company_id}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Info Card */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
            <h3 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-4">Details</h3>
            <div className="space-y-3">
              {config.configuration_type_name && (
                <div className="flex items-center justify-between">
                  <span className="text-neutral-500 dark:text-neutral-400">Type</span>
                  <span className="text-neutral-900 dark:text-white font-medium">{config.configuration_type_name}</span>
                </div>
              )}
              {config.configuration_status && (
                <div className="flex items-center justify-between">
                  <span className="text-neutral-500 dark:text-neutral-400">Status</span>
                  <span className="text-neutral-900 dark:text-white font-medium">{config.configuration_status}</span>
                </div>
              )}
              {config.location_name && (
                <div className="flex items-center justify-between">
                  <span className="text-neutral-500 dark:text-neutral-400">Location</span>
                  <span className="text-neutral-900 dark:text-white font-medium">{config.location_name}</span>
                </div>
              )}
              {config.warranty_expiration && (
                <div className="flex items-center justify-between">
                  <span className="text-neutral-500 dark:text-neutral-400">Warranty</span>
                  <span className={`font-medium ${
                    warrantyDaysLeft <= 0
                      ? 'text-red-600 dark:text-red-400'
                      : warrantyDaysLeft <= 30
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-neutral-900 dark:text-white'
                  }`}>
                    {new Date(config.warranty_expiration).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Timestamps */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
            <h3 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-4">Activity</h3>
            <div className="space-y-3 text-sm">
              {config.created_at && (
                <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400">
                  <Clock className="w-4 h-4" />
                  Created {new Date(config.created_at).toLocaleDateString()}
                </div>
              )}
              {config.updated_at && (
                <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400">
                  <Clock className="w-4 h-4" />
                  Updated {new Date(config.updated_at).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
              Delete Configuration?
            </h2>
            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
              Are you sure you want to delete "{config.name}"? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
